import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Carousel, CarouselItem } from '@americanexpress/dls-react';
import { getLocaleData } from '@/selectors/localeSelectors';
import { useCommonContent } from '@/hooks/oneCmsIntlHooks/oneCmsIntlHelperHooks';
import * as loungeHelper from '@/helpers/loungeHelper';
import { useRouteParams } from '@/context/routeParamsContext';
import { cn } from '@/helpers/utils';
import { getOneCmsLoungeFinderImagesBaseUrl } from '@/selectors/oneCmsSelectors';
import LFLink from './LFLink';
import LoungeTypeBadge from './LoungeTypeBadge';

const SearchLoungeResult = ({
  headingLevel,
  lounge,
  trackElement,
  trackDetail,
}) => {
  const { name, id } = lounge;

  const {
    lounges: { open: openLabel, closed: closedLabel, opensAt, closesAt },
    config: {
      dateFormatting: { timeFormat },
    },
  } = useCommonContent();
  const { product } = useRouteParams();
  const { locale } = useSelector(getLocaleData);
  const oneCmsBaseImgUrl = useSelector(getOneCmsLoungeFinderImagesBaseUrl);
  const placeholderImgUrl = `${oneCmsBaseImgUrl}placeholder.jpg`;
  const isLoungeTempClosed = loungeHelper.isLoungeTempClosed(lounge);

  const {
    isOpen: isLoungeOpen,
    openingTime,
    closingTime,
  } = loungeHelper.getLoungeCurrentStatus(locale, lounge, timeFormat);

  const hasInvalidTimings = !openingTime || !closingTime;

  const loungeTerminal = lounge.location?.terminal;
  const loungePath = loungeHelper.generateLoungeUrl({
    locale,
    loungeId: lounge.id,
    loungeName: lounge.name,
    loungeTerminal,
  });

  const loungeImages = lounge.images?.length
    ? lounge.images
    : [{ images: { medium: placeholderImgUrl, public_id: 'placeholder' } }];

  const loungeOperatingStartStopTime = () => {
    if (isLoungeTempClosed) return null;

    return isLoungeOpen ? (
      <span className="tw-font-normal">
        &nbsp;• {closesAt} {closingTime}
      </span>
    ) : (
      <span className="tw-font-normal">
        &nbsp;• {opensAt} {openingTime}
      </span>
    );
  };

  const DEFAULT_HEADING_LEVEL = 3;
  const level = Math.min(
    6,
    Math.max(1, Number(headingLevel || DEFAULT_HEADING_LEVEL))
  );
  const HeadingTag = `h${level}`;

  return (
    <LFLink
      className="tw-mb-2 tw-w-full tw-self-stretch sm_tw-mb-0"
      aria-label={`${lounge.name} ${loungeTerminal}`}
      to={`${product}/${lounge.airport.iata}/${loungePath}/`}
      key={`lounge-result-link-${id}`}
      detail={trackDetail || `lounge-result-link-${id}`}
      element={trackElement || 'lounge-result-link'}
    >
      <div className="tw-group tw-h-full tw-rounded-2xl tw-border tw-border-gray-300">
        <div className="tw-relative">
          <LoungeTypeBadge loungeId={id} />
          <Carousel
            isNavigationOnSlides
            isSlideFullWidth
            label="Lounge images"
            className={cn(
              'carousel-override first-child_tw-rounded-t-2xl',
              'no-touch_last-child_first-child_tw-opacity-0 no-touch_last-child_first-child_focus-within_tw-opacity-100 no-touch_last-child_first-child_group-hover_tw-opacity-100 no-touch_last-child_first-child_group-focus_tw-opacity-100',
              'no-touch_last-child_first-child_tw-duration-200 no-touch_last-child_first-child_group-hover_tw-duration-300'
            )}
            onClick={(e) => {
              if (e.target.tagName === 'IMG') return; // don't block image click through

              // don't navigate to lounge detail page when clicking on the carousel nav
              e.preventDefault();
            }}
          >
            {loungeImages.map(({ images, public_id }, i) => (
              <CarouselItem
                key={`lounge-image-${public_id}`}
                id={public_id}
                className="tw-w-full tw-flex-shrink-0"
                style={{ borderRadius: '0' }}
              >
                <img
                  src={images.medium}
                  alt={`Lounge thumbnail ${i + 1}`}
                  width={300}
                  height={200}
                  className="tw-aspect-image tw-w-full tw-object-cover"
                />
              </CarouselItem>
            ))}
          </Carousel>
        </div>
        <div className="tw-min-h-26 tw-px-6 tw-pb-6 tw-pt-4 tw-text-left">
          <HeadingTag className="tw-heading-md-medium tw-text-gray-900">
            {name}
          </HeadingTag>
          <div className="tw-body-sm tw-mt-1 tw-text-gray-800">
            <p>{loungeTerminal}</p>
            {(!hasInvalidTimings || isLoungeTempClosed) && (
              <div id={`lounge-status-${id}`}>
                {' '}
                {isLoungeOpen ? (
                  <span className="tw-text-status-success">{openLabel}</span>
                ) : (
                  <span className="tw-text-status-caution">{closedLabel}</span>
                )}
                {loungeOperatingStartStopTime()}
              </div>
            )}
          </div>
        </div>
      </div>
    </LFLink>
  );
};

SearchLoungeResult.propTypes = {
  trackElement: PropTypes.string,
  trackDetail: PropTypes.string,
  lounge: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    airport: PropTypes.shape({
      iata: PropTypes.string,
    }),
    location: PropTypes.shape({
      terminal: PropTypes.string,
    }),
    images: PropTypes.arrayOf(
      PropTypes.shape({
        images: PropTypes.shape({ medium: PropTypes.string }),
        public_id: PropTypes.string,
      })
    ),
    access_rules: PropTypes.arrayOf(
      PropTypes.shape({
        provider_id: PropTypes.string,
      })
    ),
  }),
  headingLevel: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default SearchLoungeResult;
